import React, { useEffect, useRef, useState } from 'react';
import { Menu } from '@base-ui/react/menu';
import organizationService from '../../services/organization.service';
import { UserRole } from '../../types/organization.types';
import { LedgerInput } from '../ui/LedgerInput';
import { cn } from '../../lib/cn';

interface OrganizationSwitcherProps {
  onOrganizationChange?: (role: UserRole | null) => void;
}

/**
 * The old MUI build distinguished the three org levels with the Apartment /
 * Groups / Business icons; these inline glyphs keep that visual distinction
 * while staying monochrome (currentColor) so they inherit the Ledger ink.
 */
const OrgIcon: React.FC<{ role: UserRole }> = ({ role }) => {
  const common = {
    viewBox: '0 0 16 16',
    className: 'h-4 w-4 shrink-0',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.25,
    'aria-hidden': true,
  } as const;
  if (role.department) {
    // Apartment / tower
    return (
      <svg {...common}>
        <rect x="4" y="1.5" width="8" height="13" />
        <path d="M6.4 4h.6M9 4h.6M6.4 6.5h.6M9 6.5h.6M6.4 9h.6M9 9h.6M7 14.5v-2h2v2" />
      </svg>
    );
  }
  if (role.team) {
    // Groups / people
    return (
      <svg {...common}>
        <circle cx="6" cy="6" r="2.2" />
        <path d="M2.5 13c0-2 1.6-3.2 3.5-3.2S9.5 11 9.5 13" />
        <path d="M10.6 4.3a2 2 0 0 1 0 3.4M11.2 9.9c1.5.3 2.5 1.5 2.5 3.1" />
      </svg>
    );
  }
  // Business / building
  return (
    <svg {...common}>
      <rect x="3" y="2" width="10" height="12" />
      <path d="M6 5h1M9 5h1M6 8h1M9 8h1M6.5 11h3" />
    </svg>
  );
};

const SearchIcon: React.FC = () => (
  <svg
    viewBox="0 0 16 16"
    className="h-4 w-4 shrink-0 text-ink-muted"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.25}
    aria-hidden
  >
    <circle cx="7" cy="7" r="4" />
    <path d="M10 10l3.4 3.4" />
  </svg>
);

const roleChipClass = 'cap-label shrink-0 border border-rule-faint px-1.5';

export const OrganizationSwitcher: React.FC<OrganizationSwitcherProps> = ({
  onOrganizationChange,
}) => {
  const [open, setOpen] = useState(false);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadUserRoles();
  }, []);

  const loadUserRoles = async () => {
    try {
      setLoading(true);
      const roles = await organizationService.getCurrentUserRoles();
      setUserRoles(roles || []);

      // Set the first role as selected by default
      if (roles && roles.length > 0) {
        const savedRoleId = localStorage.getItem('selectedRoleId');
        const savedRole = roles.find((r) => r.id === savedRoleId);
        setSelectedRole(savedRole || roles[0]);
      }
    } catch (error) {
      console.error('Failed to load user roles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setSearchTerm('');
  };

  const handleSelectRole = (role: UserRole) => {
    setSelectedRole(role);
    localStorage.setItem('selectedRoleId', role.id);
    setSearchTerm('');
    setOpen(false);
    onOrganizationChange?.(role);
  };

  const getOrganizationPath = (role: UserRole) => {
    const parts = [];
    if (role.companyName) parts.push(role.companyName);
    if (role.teamName) parts.push(role.teamName);
    if (role.departmentName) parts.push(role.departmentName);
    return parts.join(' › ');
  };

  const filteredRoles = userRoles.filter((role) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      role.organizationName.toLowerCase().includes(searchLower) ||
      role.role.toLowerCase().includes(searchLower) ||
      (role.companyName && role.companyName.toLowerCase().includes(searchLower)) ||
      (role.teamName && role.teamName.toLowerCase().includes(searchLower)) ||
      (role.departmentName && role.departmentName.toLowerCase().includes(searchLower))
    );
  });

  const groupedRoles = filteredRoles.reduce((acc, role) => {
    const company = role.companyName || 'Unknown';
    if (!acc[company]) {
      acc[company] = [];
    }
    acc[company].push(role);
    return acc;
  }, {} as Record<string, UserRole[]>);

  // Base UI Menu moves focus onto the first item when it opens; nudge it onto
  // the search field so typing filters immediately (matching the old TextField).
  useEffect(() => {
    if (!open) return undefined;
    const id = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  if (loading) {
    return (
      <div className="flex items-center px-2 py-1">
        <span
          data-testid="org-switcher-loading"
          className="h-4 w-4 animate-spin rounded-full border-2 border-ink/20 border-t-ink"
        />
      </div>
    );
  }

  if (userRoles.length === 0) {
    return null;
  }

  if (userRoles.length === 1) {
    const only = userRoles[0];
    return (
      <div className="px-2">
        <span className="flex w-full items-center gap-1.5 text-scale-1 text-ink">
          <OrgIcon role={only} />
          <span className="truncate">{only.organizationName}</span>
          <span className={roleChipClass}>{only.role}</span>
        </span>
      </div>
    );
  }

  return (
    <Menu.Root open={open} onOpenChange={handleOpenChange} modal={false}>
      <Menu.Trigger
        data-testid="org-switcher-trigger"
        className={cn(
          'flex w-full cursor-pointer items-center gap-1.5 px-2 py-1 text-scale-1 text-ink-muted',
          'hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent',
        )}
      >
        {selectedRole && (
          <>
            <OrgIcon role={selectedRole} />
            <span className="truncate text-ink">{selectedRole.organizationName}</span>
            <span className={roleChipClass}>{selectedRole.role}</span>
          </>
        )}
        <span aria-hidden className="ml-auto text-ink-muted">
          ▾
        </span>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner
          side="bottom"
          align="start"
          sideOffset={6}
          className="z-50"
        >
          <Menu.Popup className="w-72 border border-ink bg-paper-raised shadow-[0_2px_8px_rgba(0,0,0,0.32)]">
            <div className="p-2">
              <div className="cap-label mb-1">Switch organization</div>
              <div className="flex items-center gap-1.5">
                <SearchIcon />
                <LedgerInput
                  ref={searchRef}
                  aria-label="Search organizations"
                  placeholder="Search organizations…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  // Keep printable keys out of the Menu's typeahead/roving-focus
                  // model so they land in the search field; arrows, Enter and
                  // Escape still bubble to Base UI for navigation and close.
                  onKeyDown={(e) => {
                    if (e.key.length === 1 || e.key === 'Backspace') {
                      e.stopPropagation();
                    }
                  }}
                />
              </div>
            </div>

            <div className="border-t border-ink" />

            <div className="max-h-80 overflow-auto py-1">
              {Object.entries(groupedRoles).map(([company, roles]) => (
                <div key={company}>
                  <div className="cap-label px-3 py-1">{company}</div>
                  {roles.map((role) => {
                    const isSelected = selectedRole?.id === role.id;
                    return (
                      <Menu.Item
                        key={role.id}
                        onClick={() => handleSelectRole(role)}
                        className={cn(
                          'flex cursor-pointer items-center gap-2 px-3 py-1.5 outline-none',
                          'data-[highlighted]:bg-accent-soft',
                          isSelected && 'bg-accent-soft/60',
                        )}
                      >
                        <span className="text-ink-muted">
                          <OrgIcon role={role} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5">
                            <span className="truncate text-scale-1 text-ink">
                              {role.organizationName}
                            </span>
                            <span className={roleChipClass}>{role.role}</span>
                          </span>
                          <span className="block truncate text-scale-0 text-ink-muted">
                            {getOrganizationPath(role)}
                          </span>
                        </span>
                        {isSelected && (
                          <span aria-hidden className="shrink-0 text-accent">
                            ✓
                          </span>
                        )}
                      </Menu.Item>
                    );
                  })}
                </div>
              ))}

              {filteredRoles.length === 0 && (
                <div className="px-3 py-6 text-center text-scale-1 text-ink-muted">
                  No organizations found
                </div>
              )}
            </div>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
};
